import { TextInput, InputBase, Switch } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useDebouncedCallback } from '@mantine/hooks';
import { classNames, Select } from '@uag/react-core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input/input';

import { EInvoiceType } from 'api/models';
import { useGetCountries } from 'api/queries/countries/countries';
import { useGetIndustrialSectors } from 'api/queries/industrial-sectors/industrial-sectors';
import { InvitationSchemaType, RequestSchemaType } from './companyFormSchema';
import { CountryRule } from './CountryRule';

type Props = {
    form: Pick<
        UseFormReturnType<RequestSchemaType | InvitationSchemaType>,
        'getInputProps' | 'isDirty' | 'validateField' | 'values' | 'validate' | 'errors'
    >;
    readOnly?: boolean;
};
function validateNonEmptyFields(
    form: Pick<UseFormReturnType<RequestSchemaType | InvitationSchemaType>, 'values' | 'validateField'>,
) {
    Object.keys(form.values.companyData).forEach((key) => {
        const value = form.values.companyData[key as keyof typeof form.values.companyData];
        if (!!value && value !== '') {
            form.validateField(`companyData.${key}`);
        }
    });
}

export const CompanyBaseData = ({ form, readOnly }: Props) => {
    const { t } = useTranslation();
    const { data: countriesResponse } = useGetCountries();
    const { data: industrialSectorsResponse, isPending: areIndustrialSectorsLoading } = useGetIndustrialSectors();
    const countries = countriesResponse?.data || [];
    const industrialSectors = industrialSectorsResponse?.data || [];
    const debounceOnChangeString = useDebouncedCallback((value: string, onChangeFunction: (value: string) => void) => {
        onChangeFunction(value);
    }, 1);
    const country = countries.find((c) => c.id === form.values.companyData.country) ?? null;
    const countryRule = CountryRule.FindById(form.values.companyData.country);
    const vatIdProps = form.getInputProps('companyData.vatId');
    const useElectronicInvoiceProps = form.getInputProps('companyData.useElectronicInvoice');
    const phoneNumberProps = form.getInputProps('companyData.phoneNumber');
    const mobileNumberProps = form.getInputProps('companyData.mobileNumber');

    if (countryRule?.isFieldMandatory('useElectronicInvoice') && !useElectronicInvoiceProps.value) {
        useElectronicInvoiceProps.onChange(true);
    }

    useEffect(() => {
        validateNonEmptyFields(form);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
                <TextInput
                    className="grow"
                    label={t('country')}
                    value={country?.name ?? country?.countryCode ?? ''}
                    readOnly
                />
            </div>
            <div className="flex gap-4">
                {countryRule?.isFieldVisible('companyName') && (
                    <TextInput
                        className="grow"
                        label={t('companyName')}
                        placeholder={t('enterPlaceholder', { label: t('companyName') })}
                        readOnly={readOnly}
                        required
                        {...form.getInputProps('companyData.companyName')}
                    />
                )}
            </div>
            <div className="flex gap-4">
                {countryRule?.isFieldVisible('companyOwnerName') && (
                    <TextInput
                        className="grow"
                        label={t('companyOwnerName')}
                        placeholder={t('enterPlaceholder', { label: t('companyOwnerName') })}
                        readOnly={readOnly}
                        {...form.getInputProps('companyData.companyOwnerName')}
                    />
                )}
            </div>
            {countryRule?.isFieldVisible('industrialSector') && (
                <Select
                    data={industrialSectors.map(({ identifier, translation }) => ({
                        value: identifier,
                        label: translation || identifier,
                    }))}
                    disabled={areIndustrialSectorsLoading || industrialSectors.length <= 0}
                    label={t('industrialSector')}
                    maxDropdownHeight={200}
                    placeholder={t('selectPlaceholder', { label: t('industrialSector') })}
                    readOnly={readOnly}
                    required={countryRule?.isFieldMandatory('industrialSector')}
                    {...form.getInputProps('companyData.industrialSector')}
                />
            )}
            {countryRule?.isFieldVisible('street') && (
                <div className="flex flex-col gap-4">
                    <h4 className="text-base-bold mt-1 -mb-2">{t('address')}</h4>
                    <div className="flex gap-4">
                        <TextInput
                            className="grow"
                            label={t('streetAndStreetNumber')}
                            placeholder={t('enterPlaceholder', { label: t('streetAndStreetNumber') })}
                            readOnly={readOnly}
                            required={countryRule?.isFieldMandatory('street')}
                            {...form.getInputProps('companyData.street')}
                        />
                    </div>
                </div>
            )}
            <div className="flex gap-4">
                {countryRule?.isFieldVisible('zipCode') && (
                    <TextInput
                        className="grow-0"
                        label={t('zipCode')}
                        placeholder={t('enterPlaceholder', { label: t('zipCode') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('zipCode')}
                        {...form.getInputProps('companyData.zipCode')}
                    />
                )}

                {countryRule?.isFieldVisible('city') && (
                    <TextInput
                        className="grow"
                        label={t('city')}
                        placeholder={t('enterPlaceholder', { label: t('city') })}
                        readOnly={readOnly}
                        required
                        {...form.getInputProps('companyData.city')}
                    />
                )}

                {countryRule?.isFieldVisible('state') && (
                    <TextInput
                        className="grow"
                        label={t('state')}
                        placeholder={t('enterPlaceholder', { label: t('state') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('state')}
                        {...form.getInputProps('companyData.state')}
                    />
                )}
            </div>

            {countryRule?.isFieldVisible('useElectronicInvoice') && (
                <>
                    <h4 className="text-base-bold mt-1 -mb-2">{t('financialInformation')}</h4>
                    <Switch
                        checked={useElectronicInvoiceProps.value}
                        disabled={countryRule?.isFieldMandatory('useElectronicInvoice')}
                        label={t('digitalInvoice')}
                        {...useElectronicInvoiceProps}
                        onChange={(value) => {
                            useElectronicInvoiceProps.onChange(value);
                            form.validate();
                            if (!value) {
                                form.getInputProps('companyData.electronicInvoiceType').onChange(null);
                                form.getInputProps('companyData.electronicInvoiceEmail').onChange('');
                            }
                        }}
                    />
                </>
            )}

            {(countryRule?.isFieldMandatory('useElectronicInvoice') || useElectronicInvoiceProps.value) && (
                <div className="flex flex-col gap-4">
                    <Select
                        className="basis-2/12"
                        data={(countryRule?.getEInvoiceTypes() ?? Object.values(EInvoiceType)).map((type) => ({
                            label: t(`eInvoiceType.${type}`),
                            value: useElectronicInvoiceProps.value ? type.toString() : '',
                        }))}
                        label={t('invoiceFormat')}
                        required={form.values.companyData.useElectronicInvoice}
                        searchable={false}
                        {...form.getInputProps('companyData.electronicInvoiceType')}
                    />
                    {countryRule?.isFieldVisible('sdiCode') && (
                        <TextInput
                            className="basis-4/12"
                            label={t('sdiCode')}
                            placeholder={t('enterPlaceholder', { label: t('sdiCode') })}
                            readOnly={readOnly}
                            required={countryRule?.isFieldMandatory('sdiCode')}
                            {...form.getInputProps('companyData.sdiCode')}
                        />
                    )}
                    <TextInput
                        className="grow"
                        label={t('electronicInvoiceEmail')}
                        placeholder={t('enterPlaceholder', { label: t('electronicInvoiceEmail') })}
                        readOnly={readOnly}
                        required={form.values.companyData.useElectronicInvoice}
                        {...form.getInputProps('companyData.electronicInvoiceEmail')}
                    />
                </div>
            )}
            <div className="flex gap-4">
                {countryRule?.isFieldVisible('vatId') && (
                    <TextInput
                        className="grow"
                        label={t('vatId')}
                        placeholder={t('enterPlaceholder', { label: t('vatId') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('vatId')}
                        {...vatIdProps}
                    />
                )}
                {countryRule?.isFieldVisible('kvkNr') && (
                    <TextInput
                        className="grow"
                        label={t('kvkNr')}
                        placeholder={t('enterPlaceholder', { label: t('kvkNr') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('kvkNr')}
                        {...form.getInputProps('companyData.kvkNr')}
                    />
                )}
                {countryRule?.isFieldVisible('taxNumber') && (
                    <TextInput
                        className="grow"
                        label={t('taxNumber')}
                        placeholder={t('enterPlaceholder', { label: t('taxNumber') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('taxNumber')}
                        {...form.getInputProps('companyData.taxNumber')}
                    />
                )}
                {countryRule?.isFieldVisible('commercialRegistrationNr') && (
                    <TextInput
                        className="grow"
                        label={t('commercialRegistrationNr')}
                        placeholder={t('enterPlaceholder', { label: t('commercialRegistrationNr') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('commercialRegistrationNr')}
                        {...form.getInputProps('companyData.commercialRegistrationNr')}
                    />
                )}
            </div>
            {countryRule?.isFieldVisible('icoNumber') && (
                <TextInput
                    className="grow"
                    label={t('icoNumber')}
                    placeholder={t('enterPlaceholder', { label: t('icoNumber') })}
                    readOnly={readOnly}
                    required={countryRule?.isFieldMandatory('icoNumber')}
                    {...form.getInputProps('companyData.icoNumber')}
                />
            )}
            <div className="flex flex-col gap-4">
                {countryRule?.isFieldVisible('companyEmail') && (
                    <>
                        <h4 className="text-base-bold mt-1 -mb-2">{t('contactInformation')}</h4>
                        <TextInput
                            className="grow"
                            label={t('generalOfficeMail')}
                            placeholder={t('enterPlaceholder', { label: t('generalOfficeMail') })}
                            readOnly={readOnly}
                            required
                            {...form.getInputProps('companyData.generalOfficeMail')}
                        />
                    </>
                )}

                {countryRule?.isFieldVisible('pecEmail') && (
                    <TextInput
                        className="grow"
                        label={t('pecEmail')}
                        placeholder={t('enterPlaceholder', { label: t('pecEmail') })}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('pecEmail')}
                        {...form.getInputProps('companyData.pecEmail')}
                    />
                )}
            </div>
            <div className="flex gap-4">
                {countryRule?.isFieldVisible('phoneNumber') && (
                    <InputBase
                        className="grow"
                        classNames={classNames.textField.default}
                        component={PhoneInput}
                        label={t('phoneNumber')}
                        placeholder={countryRule?.getPlaceholder('phoneNumber', t('phoneNumberPlaceholder'))}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('phoneNumber')}
                        unstyled
                        {...form.getInputProps('companyData.phoneNumber')}
                        {...phoneNumberProps}
                        onChange={(value: string) => {
                            debounceOnChangeString(value, phoneNumberProps.onChange);
                        }}
                    />
                )}
                {countryRule?.isFieldVisible('mobileNumber') && (
                    <InputBase
                        className="grow"
                        classNames={classNames.textField.default}
                        component={PhoneInput}
                        label={t('mobileNumber')}
                        placeholder={countryRule?.getPlaceholder('mobileNumber', t('phoneNumberPlaceholder'))}
                        readOnly={readOnly}
                        required={countryRule?.isFieldMandatory('mobileNumber')}
                        unstyled
                        {...form.getInputProps('companyData.mobileNumber')}
                        {...mobileNumberProps}
                        onChange={(value: string) => {
                            debounceOnChangeString(value, mobileNumberProps.onChange);
                        }}
                    />
                )}
            </div>
        </div>
    );
};
